import { classes } from './Widget.st.css';
import React, { FC, useState, useRef, useLayoutEffect } from 'react';
import {
  TitleSection,
  LogoSection,
  DividerSection,
  ErrorSection,
  FormSwitchBarSection,
} from '../../common';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { FormStyles, StyleProvider } from '@wix/form-fields/viewer-wut';
import { ControllerProps } from './types';
import layoutParams from '../layoutParams';
import { SocialButtonsSection } from '../../common/SocialButtonsSection/SocialButtonsSection';
import {
  SignupFormSection,
  SignupFormSectionRef,
} from './FormSection/SignupFormSection';
import { SocialButtonsPlacement } from '../../Login/Settings/Tabs/LayoutTab/SocialPreset';
import { useRepromise } from '../../common/repromise';
import { useConnectionSettings } from '../../common/useConnectionSettings';
import LoginWidget from '../../Login/Widget';
import { ForcedState, TEXT_DIRECTION } from '../../../constants';
import { Close } from '@wix/wix-ui-icons-common/on-stage';
import stylesParams from '../stylesParams';
import classNames from 'classnames';
import {
  IconButton,
  IconButtonThemes,
  ButtonPriority,
  StatesButton,
  StatesButtonStates,
  ThreeDotsLoader,
} from 'wix-ui-tpa/cssVars';
import { useStyleParamLogger } from '../../utils/styleParamLogger';
import { useSettingsParamLogger } from '../../utils/settingsParamLogger';
import { useLayoutParamLogger } from '../../utils/layoutParamLogger';
import settingsParams from '../settingsParams';
import {
  ProfilePolicyFootnote,
  ConsentStatement,
  PublicProfileCheckbox,
} from './FootnoteSection';
import { classes as formClasses } from '../../common/FormSection/FormSection.st.css';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { getSidesPaddingDefault } from '../../../constants/layoutConstants';

const Widget: FC<ControllerProps> = (props) => {
  const {
    isEditor,
    isEditor3,
    isStudio2,
    isPreview,
    forceView,
    controllerSettings,
    completeProfileMode,
  } = props;
  const { isRTL, isMobile } = useEnvironment();
  const { t } = useTranslation();
  // All functions passed as props must be wrapped with repromise
  // They must be unwrapped with useRepromise before normal use
  const register = useRepromise(props.register, props);
  const loginWithIdp = useRepromise(props.loginWithIdp, props);
  const closeLightbox = useRepromise(props.closeLightbox, props);
  const completeProfile = useRepromise(props.completeProfile, props);
  const promptLogin = useRepromise(props.promptLogin, props);
  const navigateToLink = useRepromise(props.navigateToLink, props);

  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const signupFormRef = useRef<SignupFormSectionRef>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();
  const settings = useSettings();

  // Get default checkbox state from settings
  const publicProfileCheckedByDefault = settings.get(
    settingsParams.publicProfileCheckedByDefault,
  );
  const [isCommunityChecked, setIsCommunityChecked] = useState(
    publicProfileCheckedByDefault,
  );

  // Sync checkbox state with settings when the default changes (e.g., in editor)
  React.useEffect(() => {
    setIsCommunityChecked(publicProfileCheckedByDefault);
  }, [publicProfileCheckedByDefault]);

  // Monitor all style parameter changes
  useStyleParamLogger(stylesParams);

  // Monitor all settings parameter changes
  useSettingsParamLogger(settingsParams);

  // Monitor all layout parameter changes
  useLayoutParamLogger(layoutParams);

  const profilePolicyAsFootnote =
    settings.get(settingsParams.profilePolicyMessageDisplay) === 'footnote';

  const submitButtonText = completeProfileMode
    ? settings.get(settingsParams.completionButtonText)
    : settings.get(settingsParams.submitButtonText);

  const getSignupButtonClassName = () => {
    const baseClass = formClasses.signupSubmitButton;
    const isHoverForced =
      isEditor && forceView === ForcedState.SUBMIT_BUTTON_HOVER;

    return isHoverForced
      ? `${baseClass} ${formClasses.signupHover}`
      : baseClass;
  };

  const handleSubmit = () => {
    signupFormRef.current?.submit();
  };

  const hasError =
    !!error || (isEditor && forceView === ForcedState.GLOBAL_ERROR);

  const shouldShowSocialButtonsFirst =
    styles.get(layoutParams.socialButtonsFirst) ===
    SocialButtonsPlacement.FIRST;

  const { socialConnections, hasPasswordConnection, ssoMandatory } =
    useConnectionSettings({
      isEditor,
      settings: controllerSettings,
    });

  // Determine which section should get auto-focus based on layout
  const shouldFormAutoFocus =
    completeProfileMode ||
    (hasPasswordConnection && !shouldShowSocialButtonsFirst);
  const shouldSocialAutoFocus =
    !completeProfileMode &&
    socialConnections.length > 0 &&
    shouldShowSocialButtonsFirst;
  const shouldLightboxAutoFocus =
    !shouldFormAutoFocus && !shouldSocialAutoFocus;

  useLayoutEffect(() => {
    if (shouldLightboxAutoFocus && !isEditor) {
      requestAnimationFrame(() => {
        lightboxRef.current?.focus();
      });
    }
  }, [shouldLightboxAutoFocus, isEditor]);

  const checkboxSection =
    props.showPrivacyNote && !profilePolicyAsFootnote ? (
      <PublicProfileCheckbox
        checked={isCommunityChecked}
        onChange={setIsCommunityChecked}
      />
    ) : null;

  const formSection =
    hasPasswordConnection || completeProfileMode ? (
      <div key="form" className={classes.formThemeWrapper}>
        <FormStyles enabled>
          <StyleProvider>
            <SignupFormSection
              ref={signupFormRef}
              register={register}
              completeProfile={completeProfile}
              completeProfileMode={completeProfileMode}
              completeProfilePrefill={props.completeProfilePrefill}
              setError={setError}
              isEditor={isEditor}
              forceView={forceView}
              recaptchaOption={
                controllerSettings.collectionSettings?.signupRecaptchaOption
              }
              isCommunityChecked={isCommunityChecked}
              setIsCommunityChecked={setIsCommunityChecked}
              props={props}
              onLoadingChange={setIsLoading}
              closeLightbox={closeLightbox}
              checkboxSection={checkboxSection}
              autoFocus={shouldFormAutoFocus}
            />
          </StyleProvider>
        </FormStyles>
      </div>
    ) : null;

  const submitButtonSection =
    hasPasswordConnection || completeProfileMode ? (
      <StatesButton
        key="submit-button"
        data-hook="signup-submit-button"
        onClick={isPreview ? undefined : handleSubmit}
        disabled={isLoading}
        className={getSignupButtonClassName()}
        priority={ButtonPriority.primary}
        state={
          isLoading ? StatesButtonStates.IN_PROGRESS : StatesButtonStates.IDLE
        }
        idleContent={submitButtonText ?? ''}
        dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
        inProgressContent={
          <div className={formClasses.signupButtonLoaderWrapper}>
            <ThreeDotsLoader className={formClasses.signupButtonLoader} />
          </div>
        }
      >
        {submitButtonText}
      </StatesButton>
    ) : null;

  const socialSection =
    !completeProfileMode && socialConnections.length > 0 ? (
      <SocialButtonsSection
        key="social"
        enabledConnections={socialConnections}
        loginWithIdp={(idpConnectionId, joinCommunity) =>
          loginWithIdp(
            idpConnectionId,
            joinCommunity,
            signupFormRef.current?.getProfileCompletionPrefill(),
          )
        }
        setError={setError}
        isEditor={isEditor}
        isPreview={isPreview}
        forceView={forceView}
        layoutParams={layoutParams}
        settingsParams={settingsParams}
        stylesParams={stylesParams}
        autoFocus={shouldSocialAutoFocus}
      />
    ) : null;

  const dividerSection =
    !completeProfileMode &&
    hasPasswordConnection &&
    socialConnections.length > 0 ? (
      <DividerSection key="divider" formType="signup" />
    ) : null;

  const profilePolicyFootnoteSection =
    props.showPrivacyNote && profilePolicyAsFootnote ? (
      <ProfilePolicyFootnote key="profile-policy-footnote" />
    ) : null;

  const consentStatementSection = (
    <ConsentStatement key="consent-statement" navigateToLink={navigateToLink} />
  );

  const sections: React.ReactNode[] = [];
  if (formSection) {
    sections.push(
      <div key="form" className={classes.formOrder}>
        {formSection}
      </div>,
    );
  }
  if (submitButtonSection) {
    sections.push(
      <div key="submit" className={classes.submitOrder}>
        {submitButtonSection}
      </div>,
    );
  }
  if (dividerSection) {
    sections.push(
      <div key="divider" className={classes.dividerOrder}>
        {dividerSection}
      </div>,
    );
  }
  if (socialSection) {
    sections.push(
      <div key="social" className={classes.socialOrder}>
        {socialSection}
      </div>,
    );
  }
  if (profilePolicyFootnoteSection) {
    sections.push(
      <div key="footnote" className={classes.footnoteOrder}>
        {profilePolicyFootnoteSection}
      </div>,
    );
  }
  if (consentStatementSection) {
    sections.push(
      <div key="consent" className={classes.consentOrder}>
        {consentStatementSection}
      </div>,
    );
  }

  const clickOnLogin = () => {
    promptLogin({
      mode: 'login',
      allowPostAuthRedirect: props.allowPostAuthRedirect,
    });
  };

  // Complete-profile mode runs post-auth, so the login widget must not pre-empt the profile form -
  // on an SSO-mandatory site that would loop the member back to login, never completing.
  if (ssoMandatory && !isEditor && !completeProfileMode) {
    // When SSO is mandatory, render the Login Widget instead of signup form
    // The login function is not used since only SSO connections are available
    const emptyLogin = async () => {};
    const emptyPromptAuthPage = async () => {};
    const emptyPromptLogin = async () => {};

    return (
      <LoginWidget
        login={emptyLogin}
        loginWithIdp={props.loginWithIdp}
        promptAuthPage={emptyPromptAuthPage}
        promptLogin={emptyPromptLogin}
        isEditor={isEditor}
        isEditor3={isEditor3}
        isStudio2={isStudio2}
        settings={controllerSettings}
        forceView={ForcedState.REGULAR}
        closeLightbox={props.closeLightbox}
      />
    );
  }

  const errorMessage =
    isEditor && forceView === ForcedState.GLOBAL_ERROR
      ? t('memberAuthentication.common.errorSection.editorPreview')
      : error;
  const shouldShowErrorSection = Boolean(errorMessage);
  const shouldUseBuilderPopupDefaultSidesPadding =
    (isEditor3 || isStudio2) &&
    styles.get(layoutParams.sidesPadding) ===
      getSidesPaddingDefault({ isMobile });

  return (
    <div
      ref={lightboxRef}
      tabIndex={-1}
      data-hook="lightbox"
      aria-label={t(
        'memberAuthentication.ariaLabel.uouScreens.signupForm.popup',
      )}
      className={classNames(classes.lightbox, {
        [classes.shadow]: styles.get(stylesParams.enableShadow),
        [classes.hasError]: hasError,
        [classes.mobile]: isMobile,
        [classes.editor3DefaultDesktopSidesPadding]:
          shouldUseBuilderPopupDefaultSidesPadding && !isMobile,
        [classes.editor3DefaultMobileSidesPadding]:
          shouldUseBuilderPopupDefaultSidesPadding && isMobile,
      })}
    >
      <div className={classes.contentWrapper}>
        <div
          data-hook="signup-widget"
          className={classNames(classes.content, {
            [classes.contentEditor]: isEditor,
          })}
        >
          {!(isEditor3 || isStudio2) && (
            <IconButton
              icon={<Close />}
              // Prevent focus change on pointer down so the input doesn't blur before this action
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => closeLightbox()}
              className={classes.closeButton}
              data-hook="signup-close-button"
              theme={IconButtonThemes.Box}
              dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
              aria-label={t(
                'memberAuthentication.ariaLabel.uouScreens.closeButton',
              )}
            />
          )}
          <LogoSection formType="signup" />
          <TitleSection
            formType="signup"
            ssoMandatory={ssoMandatory}
            titleOverride={
              completeProfileMode
                ? settings.get(settingsParams.completionTitleText)
                : undefined
            }
            subtitleOverride={
              completeProfileMode
                ? settings.get(settingsParams.completionSubtitleText)
                : undefined
            }
          />
          {/* Only show form switch bar if SSO is not mandatory - when SSO is mandatory,
            it doesn't make sense to redirect users to signup since they must use SSO */}
          {!ssoMandatory && !completeProfileMode && (
            <FormSwitchBarSection
              formType="signup"
              onSwitchForm={clickOnLogin}
            />
          )}
          {sections}
        </div>
        {shouldShowErrorSection && (
          <div className={classes.errorSection}>
            <ErrorSection error={errorMessage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Widget;
